import { isUpperCase } from '@/commonFunctions/utils'
import { Close, ErrorIcon, Success } from '@/components/icons'
import { GAME_CANNOT_DRAW_EMPTY, SCORE_INTEGER, SCORE_LESS_ZERO, SCORE_OTHER_GAME } from '@/constants/MessageConstants'
import { MatchesHeaderTable } from '@/constants/Table'
import { Dialog, Transition } from '@headlessui/react'
import moment from 'moment'
import { Dispatch, Fragment, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ButtonPrimary } from '../button'

type Props = {
  open: boolean
  onClose: () => void
  tableData?: any[]
  onReUpload: () => void
  onUpload: () => void
  errors: any
  loadingUploadCSV: boolean
  dateFormat: string
  setDateFormat: Dispatch<SetStateAction<string>>
}

export const MatchesTableModal = ({
                                    open,
                                    onClose,
                                    tableData,
                                    onReUpload,
                                    onUpload,
                                    errors,
                                    loadingUploadCSV,
                                    dateFormat,
                                    setDateFormat,
                                  }: Props) => {
  const { t } = useTranslation()

  const [markedData, setMarkedData] = useState<any>([])
  const [listErrors, setListErrors] = useState<string[]>([])

  const calculateTeamWins = (match: any, totalGames: number) => {
    let team1Wins = 0
    let team2Wins = 0
    for (let i = 1; i <= totalGames; i += 1) {
      const gameKeyTeam1 = `game${i}` as keyof typeof match.team1
      const gameKeyTeam2 = `game${i}` as keyof typeof match.team2
      const team1Score = Number(match.team1[gameKeyTeam1])
      const team2Score = Number(match.team2[gameKeyTeam2])
      if (team1Score > team2Score) team1Wins += 1
      if (team1Score < team2Score) team2Wins += 1
    }
    return { team1Wins, team2Wins }
  }

  useEffect(() => {
    if (tableData) {
      const newErrors: string[] = [];

      if (errors) {
        if (typeof errors === 'string') {
          newErrors.push(...errors.split(';').map((s: string) => s.trim()).filter(Boolean));
        } else if (typeof errors === 'object') {
          const flatErrors = Object.values(errors).flat().filter((e): e is string => typeof e === 'string');
          newErrors.push(...flatErrors.flatMap((e: string) => e.split(';').map((s: string) => s.trim()).filter(Boolean)));
        }
      }

      // 1. HEADER EXISTENCE CHECK
      const requiredHeaders = [
        'matchType', 'event', 'date', 'scoreType', 'playerA1DuprId', 'playerB1DuprId',
        'teamAGame1', 'teamAGame2', 'teamBGame1', 'teamBGame2', 'teamAGame3', 'teamBGame3',
        'teamAGame4', 'teamBGame4', 'teamAGame5', 'teamBGame5'
      ];

      // Get keys from the first row. If no rows exist, keys is empty.
      const uploadedHeaders = tableData.length > 0 ? Object.keys(tableData[0]) : [];
      const missingHeaders = requiredHeaders.filter(header => !uploadedHeaders.includes(header));

      if (missingHeaders.length > 0) {
        // If a header is missing, stop row-by-row validation and report the column error
        newErrors.push(`Missing required columns: ${missingHeaders.join(', ')}`);

        const headerRowDisplay = {
          isError: true, // Mark status red because the file structure is invalid
          isHeaderRow: true,
          data: MatchesHeaderTable.reduce((acc, key) => ({ ...acc, [key]: key }), {})
        };

        // Map remaining data as errors because they can't be processed without correct headers
        const dataRows = tableData.map((row) => ({
          isError: true,
          data: row,
          isHeaderRow: false
        }));

        setMarkedData([headerRowDisplay, ...dataRows]);
        setListErrors(newErrors);
        return; // Exit the effect early
      }

      // 2. DATA ROW VALIDATION (Only runs if all headers are present)
      const checkedArr = tableData.map((row: any, index: number) => {
        // Spreadsheet Row 1 is headers, so index 0 is Row 2
        const rowNum = index + 2;
        let rowHasError = newErrors.some((err: string) => err.includes(`Row ${rowNum}:`));

        const mType = row['matchType'] || '';
        const dateVal = row['date'] || '';
        const pA1 = row['playerA1DuprId'] || '';
        const pA2 = row['playerA2DuprId'] || '';
        const pB1 = row['playerB1DuprId'] || '';
        const pB2 = row['playerB2DuprId'] || '';
        const sType = row['scoreType']?.toString().toUpperCase() || '';

        const gamePairs = [
          { a: row['teamAGame1'], b: row['teamBGame1'] },
          { a: row['teamAGame2'], b: row['teamBGame2'] },
          { a: row['teamAGame3'], b: row['teamBGame3'] },
          { a: row['teamAGame4'], b: row['teamBGame4'] },
          { a: row['teamAGame5'], b: row['teamBGame5'] },
        ];

        // Player 1 Check
        if (!pA1 || !pB1) {
          newErrors.push(`Row ${rowNum}: Player1's information is empty`);
          rowHasError = true;
        }

        // Match Type Check
        if (mType !== 'S' && mType !== 'D') {
          newErrors.push(`Row ${rowNum}: Match type should be S or D`);
          rowHasError = true;
        }

        const momentPattern = dateFormat === 'yyyy-MM-dd' ? 'YYYY-MM-DD' : 'M/D/YYYY';
        if (!moment(dateVal, momentPattern, true).isValid()) {
          newErrors.push(`Row ${rowNum}: Date should be ${momentPattern} format.`);
          rowHasError = true;
        }

        if (!isUpperCase(pA1) || !isUpperCase(pB1) || (pA2 && !isUpperCase(pA2)) || (pB2 && !isUpperCase(pB2))) {
          newErrors.push(`Row ${rowNum}: DuprId should be uppercase`);
          rowHasError = true;
        }

        // Player 2 / Partner Check
        if ((pA2 && !pB2) || (!pA2 && pB2)) {
          newErrors.push(`Row ${rowNum}: Player2's information is missing`);
          rowHasError = true;
        }

        // Match Type Consistency
        if (!pA2 && !pB2 && mType === 'D') {
          newErrors.push(`Row ${rowNum}: Match type should be S`);
          rowHasError = true;
        }
        if (pA2 && pB2 && mType === 'S') {
          newErrors.push(`Row ${rowNum}: Match type should be D`);
          rowHasError = true;
        }

        // Score Logic: Draws/Empty
        if (gamePairs.some(p => (p.a || p.b) && p.a === p.b)) {
          newErrors.push(`Row ${rowNum}: ${GAME_CANNOT_DRAW_EMPTY}`);
          rowHasError = true;
        }

        // Score Logic: Negative numbers
        if (gamePairs.some(p => (p.a && Number(p.a) < 0) || (p.b && Number(p.b) < 0))) {
          newErrors.push(`Row ${rowNum}: ${SCORE_LESS_ZERO}`);
          rowHasError = true;
        }

        // Score Logic: Missing Partner Game Score
        if (gamePairs.some(p => (p.a && !p.b) || (!p.a && p.b))) {
          newErrors.push(`Row ${rowNum}: ${SCORE_OTHER_GAME}`);
          rowHasError = true;
        }

        // Score Logic: Non-integers
        if (gamePairs.some(p => (p.a && !Number.isInteger(Number(p.a))) || (p.b && !Number.isInteger(Number(p.b))))) {
          newErrors.push(`Row ${rowNum}: ${SCORE_INTEGER}`);
          rowHasError = true;
        }

        // Match Result Tie Check
        const matchForWins = {
          team1: { game1: gamePairs[0].a, game2: gamePairs[1].a, game3: gamePairs[2].a, game4: gamePairs[3].a, game5: gamePairs[4].a },
          team2: { game1: gamePairs[0].b, game2: gamePairs[1].b, game3: gamePairs[2].b, game4: gamePairs[3].b, game5: gamePairs[4].b }
        };
        const { team1Wins, team2Wins } = calculateTeamWins(matchForWins, 5);
        if (team1Wins === team2Wins) {
          newErrors.push(`Row ${rowNum}: ${GAME_CANNOT_DRAW_EMPTY}`);
          rowHasError = true;
        }

        // Score Type Strict Validation
        if (sType !== 'SIDEOUT' && sType !== 'RALLY') {
          newErrors.push(`Row ${rowNum}: Score Type must be SIDEOUT or RALLY`);
          rowHasError = true;
        }

        return { isError: rowHasError, data: row, isHeaderRow: false };
      });

      // Create the visual header row (Row 1)
      const headerRowDisplay = {
        isError: false,
        isHeaderRow: true,
        data: MatchesHeaderTable.reduce((acc, key) => ({ ...acc, [key]: key }), {})
      };

      // Combine: Header at index 0, followed by the data rows
      setMarkedData([headerRowDisplay, ...checkedArr]);
      setListErrors(Array.from(new Set(newErrors)));
    }
  }, [tableData, open, errors, dateFormat]);

  useEffect(() => {
    if (!open) {
      setMarkedData([])
      setListErrors([])
    }
  }, [open])

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full flex flex-col gap-y-4 max-w-5xl max-h-[512px] transform overflow-hidden rounded-2xl bg-white px-6 py-4 text-left align-middle shadow-xl transition-all relative">
                <button className="w-9 h-9 rounded-full bg-navy-900 bg-opacity-10 flex items-center justify-center absolute right-2 top-2 hover:bg-opacity-20" onClick={onClose}>
                  <Close className="text-navy-900" />
                </button>

                <div className="flex flex-row justify-between items-end pr-10">
                  <Dialog.Title as="h3" className="text-xl text-navy-900 font-semibold">
                    {t('club.importMatches')} ({markedData.length > 0 ? markedData.length - 1 : 0})
                  </Dialog.Title>

                  <div className="flex flex-col gap-y-1 w-64 text-left">
                    <label className="text-xs font-semibold text-navy-900 opacity-70">Date Format in CSV</label>
                    <select
                      value={dateFormat}
                      onChange={(e) => setDateFormat(e.target.value)}
                      className="w-full h-8 px-2 rounded-lg border border-navy-900 border-opacity-20 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
                    >
                      <option value="yyyy-MM-dd">YYYY-MM-DD</option>
                      <option value="M/d/yyyy">MM/DD/YYYY</option>
                    </select>
                  </div>
                </div>

                {listErrors.length > 0 && (
                  <div className="flex flex-col gap-y-1 h-[72px] overflow-y-auto border border-boldOrange border-opacity-20 rounded-lg p-2 bg-boldOrange bg-opacity-5 flex-shrink-0">
                    {listErrors.map((item, index) => (
                      <p key={index} className="text-xs text-boldOrange font-medium text-left">{item}</p>
                    ))}
                  </div>
                )}

                <div className="relative overflow-x-auto">
                  <table className="w-full text-left">
                    <tbody>
                    {markedData?.map((item: any, index: number) => (
                      <tr key={index} className={item.isHeaderRow ? "bg-gray-50 font-bold" : ""}>
                        <th scope="row" className={`${item?.isError ? 'bg-boldOrange' : 'bg-green-400'} px-5 py-3 flex items-center justify-center`}>
                          {item.isHeaderRow ? null : item?.isError ? <ErrorIcon className="text-white"/> : <Success className="text-white"/>}
                        </th>
                        {MatchesHeaderTable.map((headerKey: string) => (
                          <th scope="row" className="px-6 py-2 whitespace-nowrap" key={headerKey}>
                            <p className={`text-ellipsis whitespace-nowrap overflow-hidden text-sm ${item.isHeaderRow ? 'text-navy-900 font-bold' : 'text-navy-900 font-normal'}`}>
                              {item.data[headerKey] || ""}
                            </p>
                          </th>
                        ))}
                      </tr>
                    ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end gap-x-4">
                  <button type="button" className="bg-navy-900 hover:bg-opacity-20 bg-opacity-10 rounded-full flex text-sm px-4 h-9 items-center justify-center text-navy-900 font-semibold" onClick={onReUpload}>
                    {t('common.reUpload')}
                  </button>
                  {open && <ButtonPrimary disabled={!!listErrors?.length || loadingUploadCSV} title={t('common.upload')} onClick={onUpload} />}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
